import {AbstractControl, FormArray, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import { normalizeDateString } from './normalize-date.util';
import* as googleLibphonenumber from 'google-libphonenumber';
import {ProfileField} from "../common/event.model";
import {DestroyRef} from "@angular/core";
import {startWith} from "rxjs";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

/* Email format validator */
export function strictEmailValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) {
    return null;
  }

  const strictEmailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  return strictEmailPattern.test(control.value) ? null : { invalidEmail: true };
}

/* 2 input fields have to match */
export function mustMatch(controlName: string, matchingControlName: string, errorKey: string = 'mismatch'): ValidatorFn {
  return (formGroup: AbstractControl): ValidationErrors | null => {
    const control = formGroup.get(controlName);
    const matchingControl = formGroup.get(matchingControlName);

    if (!control || !matchingControl) {
      return null;
    }

    if (control.value === matchingControl.value) {
      return null;
    }

    return { [errorKey]: true };
  };
}

/* Field can not be empty */
export function noWhitespaceValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (typeof control.value === 'string' && control.value.trim().length === 0) {
      return { whitespace: true };
    }
    return null;
  };
}

/* Field must have at least 1 letter */
export function atLeastOneLetterValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (typeof control.value !== 'string' || !control.value.trim()) {
      return null;
    }

    if (!/[A-Za-z]/.test(control.value)) {
      return { noLetter: true };
    }

    return null;
  };
}

/* Date can not be long */
export function dateValidator(control: AbstractControl): ValidationErrors | null {
  let value: string = control.value;
  if (!value) {
    return null;
  }

  value = normalizeDateString(value);
  const dateRegex = /^(0?[1-9]|[12][0-9]|3[01])[.](0?[1-9]|1[0-2])[.](19|20)\d{2}$/;
  if (!dateRegex.test(value)) {
    return { invalidDate: true };
  }

  const parts = value.split('.');
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const year = parseInt(parts[2], 10);
  const date = new Date(year, month, day);

  if (year < 1915) {
    return { invalidDate: true };
  }

  const today = new Date();
  if (date > today) {
    return { futureDate: true };
  }

  return null;
}

// “At least X years old” validator
export function minAgeValidator(minAge: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value: string = control.value;
    if (!value) return null;

    const normalizedDate = normalizeDateString(value);
    const [dd, mm, yyyy] = normalizedDate.split('.').map(s => parseInt(s, 10));
    const birth = new Date(yyyy, mm - 1, dd);
    const today = new Date();
    const cutoff = new Date(today.getFullYear() - minAge, today.getMonth(), today.getDate() - 1);

    return birth <= cutoff ? null : { tooYoung: { requiredAge: minAge } };
  };
}

/* Rules for OIB format */
export function oibValidator(control: AbstractControl): ValidationErrors | null {
  const oib = control.value;
  if (!oib) {
    return { invalidOib: true };
  }

  const normalized = oib.replace(/-/g, '');
  if (normalized.length !== 11 || !/^\d{11}$/.test(normalized)) {
    return { invalidOib: true };
  }

  let calculated = 10;
  for (const digit of normalized.substring(0, 10)) {
    calculated += parseInt(digit, 10);
    calculated %= 10;
    if (calculated === 0) {
      calculated = 10;
    }
    calculated *= 2;
    calculated %= 11;
  }

  let check = 11 - calculated;
  if (check === 10) {
    check = 0;
  }

  return check === parseInt(normalized.charAt(10), 10) ? null : { invalidOib: true };
}


/* Duplicate barcode validator */
export function duplicateBarcodeValidator(membersExclusiveFlag: () => boolean): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!membersExclusiveFlag()) {
      return null;
    }

    const array = control as FormArray;
    const codes = array.controls
      .map(group => group.get('membershipBarcode')!.value?.toString().trim())
      .filter((v: string) => !!v);

    const hasDupes = new Set(codes).size !== codes.length;
    return hasDupes ? { duplicateBarcode: true } : null;
  };
}

/* Legit Postal code in Croatia validator */
export function pttConditionalValidator(): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const country = group.get('drzava_lov')?.value;
    const pttCtrl = group.get('pbr');

    if (country !== 'HR' || !pttCtrl) {
      return null;
    }

    const ptt = pttCtrl.value as string;
    return /^\d{5}$/.test(ptt) ? null : { invalidPTT: true };
  };

  /* Phone Validator */

}

export function parseWithGoogleLibphonenumber(control: AbstractControl): ValidationErrors | null {
  /*
    Copyright (C) 2009 The Libphonenumber Authors

    Licensed under the Apache License, Version 2.0 (the "License");
    you may not use this file except in compliance with the License.
    You may obtain a copy of the License at

        http://www.apache.org/licenses/LICENSE-2.0

    Unless required by applicable law or agreed to in writing, software
    distributed under the License is distributed on an "AS IS" BASIS,
    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
    See the License for the specific language governing permissions and
    limitations under the License.
  */

  const phoneUtil = googleLibphonenumber.PhoneNumberUtil.getInstance();
  const rawInput = control.value?.toString().trim();

  if (!rawInput) return null;

  try {
    let parsedNumber;

    if (rawInput.startsWith('+')) {
      parsedNumber = phoneUtil.parse(rawInput);
    } else {
      parsedNumber = phoneUtil.parse(rawInput, 'HR');
    }

    const isValid = phoneUtil.isValidNumber(parsedNumber);
    return isValid ? null : { invalidPhoneNumber: true };

  } catch (e) {
    return { invalidPhoneNumber: true };
  }
}

export function applyCountryOibIdLogicValidation(
  group: FormGroup,
  fields: ProfileField[],
  destroyRef: DestroyRef
): void {
  const countryControl = group.get('drzavljanstvo');
  const oibControl = group.get('oib_osobe') || group.get('oib');
  const idControl = group.get('id');
  if (!countryControl || !oibControl || !idControl) return;

  const oibField = fields.find(f => f.name === 'oib_osobe' || f.name === 'oib');
  const idField = fields.find(f => f.name === 'id');
  if (!oibField || !idField) return;

  countryControl.valueChanges
    .pipe(startWith(countryControl.value), takeUntilDestroyed(destroyRef))
    .subscribe(selectedCountry => {
      oibControl.enable({ onlySelf: true, emitEvent: false });
      idControl.enable({ onlySelf: true, emitEvent: false });
      oibField.mandatory = false;
      idField.mandatory  = false;

      if (selectedCountry === 'HR') {
        idControl.disable({ onlySelf: true, emitEvent: false });
        oibField.mandatory = true;
      } else if (selectedCountry) {
        oibControl.disable({ onlySelf: true, emitEvent: false });
        idField.mandatory = true;
      }

      const oibValidators = [];
      if (oibField.mandatory) {
        oibValidators.push(Validators.required);
      }

      if (oibField.length) {
        oibValidators.push(Validators.maxLength(oibField.length));
      }

      if (oibField.name === 'oib_osobe' || oibField.name === 'oib') {
        oibValidators.push(oibValidator);
      }
      oibControl.setValidators(oibValidators);

      const idValidators = [];
      if (idField.mandatory) {
        idValidators.push(Validators.required);
      }

      if (idField.length) {
        idValidators.push(Validators.maxLength(idField.length));
      }
      idControl.setValidators(idValidators);

      oibControl.updateValueAndValidity({ emitEvent: false });
      idControl.updateValueAndValidity({ emitEvent: false });
    });
}

